import _botEx from "assets/configs/botEx.json";
import { __chats } from "Utils/Chats/Chats";
import { __callbacks } from "../Callbacks/Callback";
import { __chatState } from "../Chats/ChatState";
import { __sb } from "../SB/sb";
import { __ajax } from "../AJAX/ajax";
import { __debug } from "../Debug/Debug";
import { IBotEx, IBotExMessage, IBotResponseCB, IChatMessage, IChatMessageFragment } from "../Interfaces";
import { __ai } from "Utils/AI/ai";
import { __config } from "../Configs/Config";
import { ChatMessage } from "../Chats/ChatMessage";
import { MsgForm } from "../Explorer/MsgForm";
import { __utils } from "../Utils";
import { ChatMessageFragment } from "../Chats/ChatMessageFragment";

class Bot {
    private bot: IBotEx = _botEx;
    private botType = "remote";
    public botStatus = "in-progress";
    private botTimer!: NodeJS.Timeout;
    private botMaxRemoteFetchTime = 10000; // 10 seconds
    private Id: string = "0000x0af";

    public get id(): string {
        return this.Id;
    }
    public get botName(): string {

        // Check the actual data inside this.bot.botName
        if (this.bot.botName === null || this.bot.botName === '') {
            return "Bot";
        } else {
            return this.bot.botName;
        }
    }


    private respondedUserOptions: string[] = [];
    private chatId: string = "";

    public init() {
        try {
            this.initListeners();
            this.initAppConfigRemote();
            this.handleLateResponse();
        } catch (error) {
            console.error("Initialization error:", error);
        }
    }

    public subscribe(callback: Function) {
        if (this.botStatus === "finished") {
            callback(this.getCallbackData());
        } else {
            __callbacks.config.register(callback);
        }
    }

    private initAppConfigRemote() {
        this.setStatusInProgress();
        this.fetchRemoteConfig();
    }

    private fetchRemoteConfig() {

        const queryParams = new URLSearchParams(window.location.search);
        const uid = queryParams.get('chatCode'); // Assuming chatCode is the unique identifier for the chat
        const domain = queryParams.get('url'); // This is the parent URL where the widget is installed

        const jsonData = {
            uid: uid,
            domain: domain
        };
        __ajax.sendAjaxRequest('post', '/chat/botLoad', JSON.stringify(jsonData), (res: any) => {

            try {
                const parsedRes = res;
                this.handleResponse(parsedRes);
            } catch (error) {
                console.error("Failed to parse remote config JSON:", error);
                this.finishFetchLocal();
            }
        });
    }

    private handleResponse(res: any) {
        try {
            this.bot = res;
            this.onFinishFetchRemote();
        } catch (err) {
            console.error("Invalid JSON Configuration! Received invalid JSON from bot query.", err);
            this.finishFetchLocal();
        }
    }

    private handleLateResponse() {
        this.botTimer = setTimeout(() => {
            this.finishFetchLocal();
            console.warn("Too late to fetch settings from the remote URL.");
        }, this.botMaxRemoteFetchTime);
    }

    public getAppConfig(): IBotEx {
        return this.bot;
    }

    public setStatusInProgress() {
        this.botStatus = "in-progress";
    }

    public setStatusFinished() {
        this.botStatus = "finished";
        localStorage.setItem('botStatus' + this.getUid(), this.botStatus);
    }

    private setLocalConfig() {
        this.botType = "local";
    }

    private setRemoteConfig() {
        this.botType = "remote";
    }

    private getCallbackData() {
        return {
            botStatus: this.botStatus,
            bot: this.bot,
            botType: this.botType
        };
    }

    private finishFetchLocal() {
        clearTimeout(this.botTimer);
        this.setLocalConfig();
        __callbacks.config.trigger(this.getCallbackData());
    }

    private onFinishFetchRemote() {
        clearTimeout(this.botTimer);
        this.setRemoteConfig();
        __callbacks.config.trigger(this.getCallbackData());
    }

    public setChatId(chatId: string) {
        this.chatId = chatId;
        this.loadFromChatState();
    }

    private initListeners() {
        __callbacks.onChatInputSubmit(this.handleChatInputSubmit.bind(this));
        __callbacks.onUserResponse(this.handleUserResponse.bind(this));
        __callbacks.onChatId(this.setChatId.bind(this));
        __callbacks.onStartNewChat(this.handleStartNewChat.bind(this));
        __callbacks.onStartNewChat(this.handleStartNewChat.bind(this));
        __callbacks.onFormSubmit(this.handleFormSubmit.bind(this));
    }

    private handleChatInputSubmit = (data: any) => {

        console.log('handleChatInputSubmit');

        if (!data.userSubmit || !data.textAreaValue) return;

        if (this.botStatus === "finished") return;

        const message = data.textAreaValue.trim().toLowerCase();
        const contact = localStorage.getItem('contact_id' + this.getUid());

        localStorage.setItem('bot_conversation' + this.chatId, 'true');

        const agentRequestPhrases = [
            "talk to agent", "talk to someone", "agent", "person", "transfer", "transfer me", "chat with agent", "chat with someone",
            "customer service", "human", "live support", "real person", "live agent",
            "connect to agent", "speak to agent", "speak to a person", "speak to someone",
            "contact support", "help from a person", "support agent", "support person",
            "customer rep", "customer representative", "talk to human"
        ];

        if ((__config.config.app.enable_chat === true && agentRequestPhrases.includes(message)) || (__config.config.app.ai === false)) {
            this.startChatWithAgent(false);
            return;
        }

        if (__config.config.app.ai === true) {

            const conversation = this.getConversationHistory();
            const queryParams = new URLSearchParams(window.location.search);
            const uid = queryParams.get('chatCode'); // Assuming chatCode is the unique identifier for the chat
            const domain = queryParams.get('url'); // This is the parent URL where the widget is installed

            const args = {
                uid: this.getUid(),
                question: data.textAreaValue,
                conversation: conversation,
                search_type: this.bot.botSearchType,
                domain: domain
            };

            var cid = __utils.getUuidv4();
            const chatMessage: IChatMessage = {
                id: cid,
                chatId: this.chatId,
                messageNo: Date.now(),
                time: Date.now(),
                messageType: "MessageSend",
                props: { text: data.textAreaValue },
                fragments: [
                    new ChatMessageFragment({
                        id: cid,
                        chatId: this.chatId,
                        messageNo: Date.now(),
                        messageType: "MessageSend",
                        fragmentType: "text",
                        time: Date.now(),
                        props: { text: data.textAreaValue }
                    })
                ]
            };

            __sb.sendMessage(chatMessage.chatId, new ChatMessage(chatMessage), data.textAreaValue.toString(), true);

            __utils.Sleep(250);

            __ajax.sendAISearchRequest('POST', '', JSON.stringify(args), (res: any) => {
                const botResponse: IBotExMessage[] = [{
                    id: __utils.getUuidv4(),
                    type: "text",
                    value: res.answer,
                    fragment: false
                }];

                var cid = __utils.getUuidv4();
                const botChatMessage: IChatMessage = {
                    id: cid,
                    chatId: this.chatId,
                    messageNo: Date.now(),
                    time: Date.now(),
                    messageType: "MessageReceive",
                    props: { text: res.answer },
                    fragments: [
                        new ChatMessageFragment({
                            id: cid,
                            chatId: this.chatId,
                            messageNo: Date.now(),
                            messageType: "MessageReceive",
                            fragmentType: "text",
                            time: Date.now(),
                            props: { text: res.answer }
                        })
                    ]
                };

                __sb.sendMessageBot(botChatMessage.chatId, new ChatMessage(botChatMessage), res.answer.toString());

                __callbacks.botResponse.trigger(this.formatResponse(botResponse));
            });
        } else {
            const unknownResponse: IBotExMessage[] = this.getUnknownResponse();
            __callbacks.botResponse.trigger(this.formatResponse(unknownResponse));
            this.respondedUserOptions.push("unknown");
            this.saveChatState();
        }
    }

    public startChatWithAgent(fromChatButton: boolean) {

        localStorage.setItem('bot_conversation' + this.chatId, 'false');

        if (fromChatButton) {

            // Creating a chat message to be sent when the button is clicked
            const cid = __utils.getUuidv4();
            const chatMessage: IChatMessage = {
                id: cid,
                chatId: this.chatId,
                messageNo: Date.now(),
                time: Date.now(),
                messageType: "MessageSend",
                props: { text: "Start chat with agent" },
                fragments: [
                    new ChatMessageFragment({
                        id: cid,
                        chatId: this.chatId,
                        messageNo: Date.now(),
                        messageType: "MessageSend",
                        fragmentType: "text",
                        time: Date.now(),
                        props: { text: "Start chat with agent" }
                    })
                ]
            };

            // Adding the message to the chat system
            __chats.addMessage(new ChatMessage(chatMessage));

        }

        __utils.Sleep(500);

        const botEnding: IBotExMessage[] = this.getFormResponse();
        const formattedResponse = this.formatResponse(botEnding);

        __callbacks.botResponse.trigger(formattedResponse);

        this.respondedUserOptions.push("unknown");
        this.saveChatState();

        this.botStatus = "finished";
        this.setStatusFinished();

        const chatid = this.chatId;
        setTimeout(() => {
            const contact = localStorage.getItem('contact_id' + this.getUid());
            if (!contact) {
                new MsgForm(chatid);
            }
        }, 250);
    }

    private getConversationHistory(): { who: string, response: string }[] {
        const chat = __chats.get()[this.chatId] as ChatMessage[];
        const history: { who: string, response: string }[] = [];

        if (chat) {
            chat.forEach(message => {
                const who = message.messageType === "MessageSend" ? "user" : "bot";
                let response = "";

                if (message.props && message.props.text) {
                    response = message.props.text;
                } else {
                    if (message.fragments && message.fragments.length > 0 && message.fragments[0].props && message.fragments[0].props.text) {
                        response = message.fragments[0].props.text;
                    } else {
                        console.warn(`Missing text property in message: ${JSON.stringify(message)}`);
                    }
                }

                history.push({ who, response });
            });
        } else {
            console.warn(`No chat history found for chatId: ${this.chatId}`);
        }

        return history;
    }

    private handleUserResponse = (id: string) => {
        const botResponse: IBotExMessage[] = this.getResponse(id);
        __callbacks.botResponse.trigger(this.formatResponse(botResponse));
    }

    private handleStartNewChat = () => {
        this.respondedUserOptions = [];
        this.botStatus = "in-progress";
        localStorage.setItem('botStatus' + this.getUid(), this.botStatus);
    }

    private handleFormSubmit = () => {
        const botResponse: IBotExMessage[] = this.bot.responses["onFormSubmit"];
        __callbacks.botResponse.trigger(this.formatResponse(botResponse));
    }

    public getResponse(id: string): IBotExMessage[] {
        let botResponse: IBotExMessage[] = this.bot.responses[id];
        if (!botResponse) {
            this.respondedUserOptions.push("unknown");
            botResponse = this.getUnknownResponse();
        }
        return botResponse;
    }

    private getUnknownResponse(): IBotExMessage[] {
        return this.bot.responses["unknown"];
    }

    public getFormResponse(): IBotExMessage[] {
        return this.bot.responses["onFormSubmit"];
    }

    public saveChatState() {
        if (!this.chatId) return;
        __chatState.override({
            chatId: this.chatId,
            respondedUserOptions: [...this.respondedUserOptions]
        });
        localStorage.setItem('botStatus' + this.getUid(), this.botStatus);
    }

    public loadFromChatState() {
        const chatState = __chatState.get(this.chatId);
        if (chatState?.respondedUserOptions) {
            this.respondedUserOptions = [...chatState.respondedUserOptions];
            this.botStatus = localStorage.getItem('botStatus' + this.getUid()) || 'in-progress';
        } else {
            this.respondedUserOptions = [];
            this.botStatus = 'in-progress';
        }
    }

    public formatResponse(botMsg: IBotExMessage[]): IBotResponseCB {
        return {
            chatId: this.chatId,
            response: botMsg
        };
    }

    private getUid(): string {
        return window.location.search.substr(1).split("&")[0].split("=")[1];
    }

}

export const __bot = new Bot();
